<script lang="ts" setup>
  import { ref, watch, onBeforeUnmount, onMounted, computed, type Ref } from 'vue';
  import { useShared, useHttpClient, useRoute } from '@othercommerce/foundation';

  const shared = useShared();
  const route = useRoute();
  const http = useHttpClient();

  const country: Ref<string> = ref(shared.value.activeCountryCode);
  const currency: Ref<string> = ref(shared.value.activeCurrencyCode);
  const locale: Ref<string> = ref(shared.value.locales.active);

  const switcherHtml = ref();
  const open = ref(false);

  const activeLocale = computed(() => shared.value.locales.active.toUpperCase());
  const activeCurrency = computed(() => shared.value.activeCurrencyCode.toUpperCase());

  function onClickOutside(event: MouseEvent) {
    if (switcherHtml.value && !switcherHtml.value.contains(event.target as Node)) {
      open.value = false;
    }
  }

  function toggleCountry(event) {
    http.post(route('web.country.store'), { country: event.target.value });
  }

  function toggleCurrency(event) {
    http.post(route('web.currency.store'), { currency: event.target.value });
  }

  function toggleLocale(event) {
    http.post(route('web.locale.store'), { locale: event.target.value });
  }

  onMounted(() => {
    document.addEventListener('click', onClickOutside);
  });

  onBeforeUnmount(() => {
    document.removeEventListener('click', onClickOutside);
  });

  watch(() => shared.value.activeCountryCode, (value) => {
    country.value = value;
  });

  watch(() => shared.value.activeCurrencyCode, (value) => {
    currency.value = value;
  });

  watch(() => shared.value.locales.active, (value) => {
    locale.value = value;
  });
</script>
<template>
  <div ref="switcherHtml" class="region-selector">
    <div
      :aria-label="activeLocale + '/' + activeCurrency + ', ' +  $t('SHOP.COMMON.ARIA.CLICK_TO_SET_COUNTRY_LANGUAGE_AND_CURRENCY')"
      aria-controls="region-selector-modal"
      aria-haspopup="dialog"
      class="region-selector__btn"
      role="button"
      tabindex="0"
      @click="open = !open"
      @keydown.space.enter.prevent="open = !open"
    >
      <SfFlag :code="shared.activeCountryCode" aria-hidden="true" gradient="real-linear" has-border has-border-radius has-drop-shadow size="m" />
      {{ activeLocale + '/' + activeCurrency }}
    </div>
    <UiModal id="region-selector-modal" v-model:open="open" aria-labelledby="region-selector-title" medium>
      <SfModalHeader id="region-selector-title" v-model:open="open" headerType="span" headingStyle>
        {{ $t('SHOP.COMMON.HEADER.SELECT_COUNTRY_LANGUAGE_AND_CURRENCY') }}
      </SfModalHeader>
      <div class="region-selector__content">
        <SfFormSelect v-model="country" :label="$t('SHOP.COMMON.LABEL.SHIPPING_COUNTRY')" :options="shared.countries" required @change="toggleCountry" />
        <SfFormSelect v-model="locale" :label="$t('SHOP.COMMON.LABEL.LANGUAGE')" :options="shared.locales.available" required @change="toggleLocale" />
        <SfFormSelect v-model="currency" :label="$t('SHOP.COMMON.LABEL.CURRENCY')" :options="shared.currencies" required @change="toggleCurrency" />
      </div>
    </UiModal>
  </div>
</template>
<style lang="scss" scoped>
  @use "@storefront/styles/mixins/media";

  .region-selector {
    display: flex;
    align-items: center;
    position: relative;

    &__btn {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      cursor: pointer;
      height: 2rem;

      @media (max-width: 992px) {
        padding-right: 1rem;
      }
    }

    &__content {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }
  }
</style>
